import { requireLevelUp2026 } from '../utils/access-conditions'
import type { AppId, AppAccessConfig } from '../types/app-access'

/**
 * Maps each ClientPortal app to its access rule.
 *
 *
 * @example
 * dashboard: { enabled: true },  // no permission required
 * communities: {
 *   enabled: true,
 *   permission: 'communitiesEnabled',
 *   conditions: [requirePermission('membershipEnabled')],
 * }
 */
export const APP_ACCESS_CONFIG: AppAccessConfig = {
  dashboard: { enabled: true },
  // The Discover *page* (sidebar item, /discover route guard, default landing, "See all" targets)
  // needs the portal toggle and the Levelup-2026 flag. Discover listings on course surfaces
  // (/courses, /courses/discover) are not gated by this rule.
  discover: { enabled: true, permission: 'discoverEnabled', conditions: [requireLevelUp2026] },
  courses: { enabled: true, permission: 'membershipEnabled' },
  communities: { enabled: true, permission: 'communitiesEnabled' },
  documents: { enabled: true, permission: 'sharedFilesEnabled' },
  invoices: { enabled: true, permission: 'subscriptionsEnabled' },
  estimates: { enabled: true, permission: 'estimatesEnabled' },
  contracts: { enabled: true, permission: 'contractsEnabled' },
  appointments: { enabled: true, permission: 'appointmentsEnabled' },
  events: { enabled: true, permission: 'eventsEnabled' },
  affiliates: { enabled: true, permission: 'affiliateManagerEnabled' },
  loyalty: { enabled: true, permission: 'loyaltyEnabled' },
}

/**
 * Route path prefixes for app access check (order: longest first for correct matching)
 */
export const APP_ROUTE_PREFIXES: { path: string; appId: AppId }[] = [
  { path: '/dashboard', appId: 'dashboard' },
  { path: '/courses', appId: 'courses' },
  { path: '/communities', appId: 'communities' },
  { path: '/shared-files', appId: 'documents' },
  { path: '/documents', appId: 'documents' },
  { path: '/invoices', appId: 'invoices' },
  { path: '/estimates', appId: 'estimates' },
  { path: '/contracts', appId: 'contracts' },
  { path: '/appointments', appId: 'appointments' },
  { path: '/events', appId: 'events' },
  { path: '/affiliates', appId: 'affiliates' },
  { path: '/loyalty', appId: 'loyalty' },
  // Kept last on purpose: `APP_IDS` below preserves this order, and `getAppIdFromRouteName` does a
  // first-match over it. A route name like `courses-discover` must resolve to `courses`, so
  // `discover` has to rank after every section that owns a `/discover` sub-route.
  { path: '/discover', appId: 'discover' },
]

/**
 * Get app ID from route path for access check
 */
export function getAppIdFromPath(path: string): AppId | null {
  if (!path || path === '/') return null
  const match = APP_ROUTE_PREFIXES.find(({ path: p }) => path === p || path.startsWith(p + '/'))
  return match?.appId ?? null
}

/** Distinct app ids that own a route (derived from the prefix map — single source of truth). */
const APP_IDS: AppId[] = [...new Set(APP_ROUTE_PREFIXES.map(({ appId }) => appId))]

/**
 * App ID from a Nuxt route name (hyphen-delimited segments) — classifies routes whose URL has no
 * section prefix, e.g. GoKollab tenant slugs. Exact-segment match (no substrings), checks every appId.
 */
export function getAppIdFromRouteName(routeName: string | undefined | null): AppId | null {
  if (!routeName) return null
  const segments = routeName.split('-')
  return APP_IDS.find(appId => segments.includes(appId)) ?? null
}
